import { convert, convertFlexi } from '@/utils/mrxEngine';
import { useNoteStore } from '@/features/text-mode/stores/note.store';
import { usePreferenceStore } from '@/stores/preference';
import { useGuidedModeStore } from '@/features/guided-mode/stores/guidedMode';

type Panel = any;
type Response = any;

export interface Track {
  panels: Panel[];
}

const getPanelsWithComputation = (panels: Panel[]) => panels.filter((p: Panel) => p.computation)

export function getSelectedResponses(track: Track, name: string): Response[] {
  let resps: Response[] = []
  try {
    let panel = track.panels.find((p: Panel) => p.name === name) || {}

    resps = panel.responses.filter((r: Response) => r.selected)
  }
  catch (e) {}
  return resps
}

export function getRdtStatus(track: Track): string {
  const responses = getSelectedResponses(track, 'rdt_bioline_capture_practitioner')
  return rdtStatusFromCapture(responses[0]?.value)
}

export function rdtStatusFromCapture(response: unknown): string {
  if (response === 'timedout') {
    return 'expired'
  } else if (response === 'invalid') {
    return 'invalid'
  } else if (response === 'positive_pf') {
    return 'positive'
  } else if (response === 'negative') {
    return 'negative'
  }
  return 'not_started'
}

async function assignValue(payload: Response[], value: string): Promise<Response[]> {
  try {
    let res = payload.find((x: Response) => x.value === value)
    res.selected = true
  }
  catch (e) { console.log(e) }
  return payload
}

export const computations: Record<string, (payload: Response[], track: Track) => any> = {
  // Mirrors v6's computed/actions.js:11; no migration configures computation 'ping'.
  ping: async () => {},

  async rdtStatus(payload: Response[], track: Track) {
    let status = getRdtStatus(track)
    try {
      payload.forEach((response: Response) => {
        response.selected = response.value === status
      })
    }
    catch (e) { console.log(e) }
    return payload
  },

  async assignFemaleGender(payload: Response[]) {
    return assignValue(payload, 'female')
  },

  async assignYes(payload: Response[]) {
    return assignValue(payload, 'yes')
  },

  async assignNo(payload: Response[]) {
    return assignValue(payload, 'no')
  },

  fillResponseUnknown(payload: Response[]) {
    try {
      if (payload.length === 1) {
        payload[0].unknown = true
      }
      else {
        assignValue(payload, 'unknown');
      }
    }
    catch (e) { console.log(e); }

    return payload;
  },
}

export async function preferences(): Promise<Panel[]> {
  let results: Panel[] = []
  try {
    results = JSON.parse(JSON.stringify(usePreferenceStore().all))
  }
  catch (e) { console.log(e) }
  return results
}

export async function preferenceResponses(): Promise<Response[]> {
  let results: Response[] = []
  try {
    let prefs = await preferences()
    prefs.reduce((prefResponses: Response[], panel: Panel) => {
      if (!panel.computation) {
        panel.responses.forEach((response: Response) => {
          if (response.selected) {
            prefResponses.push({ ...response, panel: panel.name, type: panel.type, input: panel.input })
          }
        })
      }
      return prefResponses
    }, results)
  }
  catch (e) { console.log(e) }
  return results
}

export async function computedResponses(track: Track): Promise<Response[]> {
  let results: Promise<Response>[] = []
  try {
    let computed = getPanelsWithComputation(track.panels || [])
    results = computed.map(async (panel: Panel) => {
      let responses = await computations[panel.computation](JSON.parse(JSON.stringify(panel.responses)), track)
      let selected = responses.find((r: Response) => r.selected) || responses[0]
      return { ...selected, panel: panel.name, type: panel.type, input: panel.input }
    })
  }
  catch (e) { console.log(e) }
  return Promise.all(results)
}

export async function gatherResponses(track: Track): Promise<Response[]> {
  let responses = (track.panels || []).reduce((preppedResponses: Response[], panel: Panel) => {
    const panelWasAnswered = panel.responses.some((r: Response) => r.selected)
    if (!panel.computation && panelWasAnswered) {
      panel.responses.forEach((response: Response) => {
        preppedResponses.push({ ...response, panel: panel.name, type: panel.type, input: panel.input })
      })
    }
    return preppedResponses
  }, [])

  const promised = await Promise.all([preferenceResponses(), computedResponses(track)])
  const prepared = responses.concat(promised.flat())

  const noteStore = useNoteStore();
  const guidedModeStore = useGuidedModeStore();
  let flexiObservations = [
    ...noteStore.observationsForConversion,
    ...guidedModeStore.observationsForConversion,
  ];
  const flexiRdt = flexiObservations.find(observation => observation.name === 'rdt_bioline_capture_practitioner');
  if (flexiRdt) {
    // Guided/text capture does not select the legacy panel that its computed status reads.
    flexiObservations = flexiObservations.filter(observation => observation.name !== 'rdt_status');
    flexiObservations.push({ type: 'response', name: 'rdt_status', value: rdtStatusFromCapture(flexiRdt.value) });
  }

  try {
    const [legacyResponses, flexiResponses] = await Promise.all([
      convert(prepared),
      convertFlexi(flexiObservations)
    ])
    return [
      ...legacyResponses.filter((response: Response) => !(flexiRdt && response.name === 'rdt_status')),
      ...flexiResponses,
    ]
  } catch (e) {
    const sentry = (window as any).App?.$sentry;
    sentry?.withScope((scope: any) => {
      scope.setTag("location", "gather-responses")
      sentry.captureException(e)
    });
    return [] // return empty array for mrx assess to error on no responses
  }
}
